import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import {
  CatDocsSearchParams,
  Catalog,
  CatalogStatusEnum,
  CatDoc,
  CatDocStatusEnum,
  CatDocFileWrapper,
  CatDocStateTagEnum,
} from 'src/app/shared/models/cat.model';
import {
  DataElement,
  newCatSystemVersion,
  PaginationParams,
  ProcessRunStatusEnum,
} from 'src/app/shared/models/common';
import { TabIdList } from 'src/app/shared/models/tab.model';
import { ServiceMetaData } from 'src/app/shared/models/service-meta.model';
import { NgRedux } from '@redux/redux-compatibility.service';
import { AppState, actionsList } from 'src/app/shared/redux/store';
import { CatService } from 'src/app/shared/services/cat.service';
import { Router } from '@angular/router';
import { SharedService } from 'src/app/shared/services/shared.service';
import { keyCodes } from 'src/app/shared/models/key-codes.model';
import { AuthService } from 'src/app/shared/services/auth.service';
import { TranslateService } from '@ngx-translate/core';
import { IntegrationService } from 'src/app/shared/services/integration.service';
import { Location } from '@angular/common';
import { TableComponent } from 'src/app/shared/components/ui/table/table.component';
import { Unsubscribe } from 'redux';
import { CommonService } from '@shared/services/common.service';

@Component({
    selector: 'cat-docs',
    templateUrl: './cat-docs.component.html',
    styleUrls: ['./cat-docs.component.scss'],
    standalone: false
})
export class CatDocsComponent implements OnInit {
  @Input() selectMode: boolean;
  @Input() viewDocNavUrl: string;
  @Input() navMode: string;
  @Input() createDocType: string;
  @Output() onDocsSelected = new EventEmitter<CatDoc[]>();

  @ViewChild('tblDocs') tblDocs: TableComponent;
  @ViewChild('tblVersions') tblVersions: TableComponent;

  loading: boolean;
  rangeFrom: number;
  rangeTo: number;
  totalRecords: number;
  searchParams: CatDocsSearchParams = {};
  filterStatus: DataElement;
  filterDocIdName: string;
  filterInactive: boolean;
  selectedDoc: CatDoc;
  displayAddCatDoc: boolean;
  displayAddBulkDoc: boolean;
  createVersionParentDoc: CatDoc;
  getProcStatusFailedCnt: number = 0;
  processStatusTimers: ReturnType<typeof setInterval>[] = [];
  getProcessStatusIntervalSeconds: number = 5;
  displayProcessStatus: boolean;
  curDoc: CatDoc;
  selectedDocs: CatDoc[] = [];
  selectedVersions: CatDoc[] = [];
  docsBeingEdited: object = {};
  docsBeingUpdated: object = {};
  dlMode: boolean = false;
  un: Unsubscribe;

  newCatSystemVersion = newCatSystemVersion;

  get docs(): CatDoc[] {
    return this.ngRedux.getState().catDocs;
  }

  get curCat(): Catalog {
    return this.ngRedux.getState().curCat;
  }

  get allowEdit(): boolean {
    if (!this.curCat || this.selectMode) return false;
    return this.curCat.statusCode == CatalogStatusEnum.DRAFT;
  }

  get catDocStatusList(): DataElement[] {
    return this.ngRedux.getState().catDocStatusList;
  }

  get docsMetaData(): ServiceMetaData {
    const metaData: ServiceMetaData = this.ngRedux.getState().catDocsMetadata;
    return metaData || {};
  }

  get displayClearFilter(): boolean {
    return (
      this.filterDocIdName != null ||
      this.filterStatus != null ||
      this.filterInactive
    );
  }

  get allowTagDocs() {
    return this.authService.currentUser.isAdmin && !this.curCat.extSw;
  }

  get allowParentSelect() {
    return this.selectMode;
  }

  get allowVersionSelect() {
    return this.allowTagDocs;
  }

  get labelingExists(): boolean {
    return (this.docs ?? []).some(
      (d) => d?.assetType?.text === 'Reference - Labeling'
    );
  }

  get smpcExists(): boolean {
    return (this.docs ?? []).some(
      (d) => d?.assetType?.text === 'SMPC'
    );
  }

  get prescribingInfoExists(): boolean {
    return (this.docs ?? []).some(
      (d) => d?.assetType?.text === 'Prescribing Information'
    );
  }

  constructor(
    private ngRedux: NgRedux<AppState>,
    private catService: CatService,
    private router: Router,
    private sharedService: SharedService,
    private authService: AuthService,
    private translateService: TranslateService,
    private integrationService: IntegrationService,
    private location: Location,
    private commonService: CommonService
  ) {
    this.un = this.ngRedux.subscribe(() => {
      const appState = this.ngRedux.getState();
      switch (appState._currentAction) {
        case actionsList.USER_SIGN_OUT:
          this.processStatusTimers.forEach((t) => {
            clearInterval(t);
          });
          break;
      }
    });
  }

  ngOnInit(): void {    
    this.dlMode = this.ngRedux.getState().dlCatDoc && !this.ngRedux.getState().dlCatDoc.dlDocHandled;
    this.loadDocs(true);
  }

  ngOnDestroy(): void {
    this.processStatusTimers?.forEach((t) => {
      clearInterval(t);
    });
    if (this.un) {
      this.un();
    }
  }

  loadDocs(newSearch: boolean) {
    this.loading = true;

    if (newSearch) {
      this.searchParams.docId = null;
      this.searchParams.searchId = null;
      this.searchParams.rangeFrom = 1;
      this.searchParams.rangeTo = 10;
    }    
    
    if (this.dlMode) {
      this.curCat.catVersionId = this.ngRedux.getState().dlCatDoc.catVersionId;
      this.searchParams.docId = this.ngRedux.getState().dlCatDoc.docId;
      this.searchParams.rangeTo = 2;
    }

    this.searchParams.docIdName = this.filterDocIdName;
    this.searchParams.status = this.filterStatus;
    this.searchParams.includeInactiveSw = this.filterInactive;

    this.processStatusTimers.forEach((t) => {
      clearInterval(t);
    });

    this.processStatusTimers = [];
    this.getProcStatusFailedCnt = 0;

    this.catService
      .getCatDocs(this.searchParams, this.curCat.catVersionId)
      .subscribe(
        (data) => {
          this.loading = false;

          let processingDocs = this.getProcessingDocs(this.docs);
          if (processingDocs.length > 0) {
            this.handleProcessingDocs(processingDocs);
          }

          this.sharedService.parseRunStatusMessageIfJSON(data);
          
          if (this.dlMode && data.length > 0 ) {

            if(data[0].isProcessRunning){
              this.dlMode = false;
              this.ngRedux.dispatch({
                type: actionsList.SET_DL_CAT_DOC,
                data: null,
              });
              this.clearFilter();
            }else {
              this.view(data[0]);
            }           
                        
          } else { 
            this.ngRedux.dispatch({
              type: actionsList.SET_DL_CAT_DOC,
              data: null,
            });
          }

      },
        (err) => (this.loading = false)
      );
  }

  private getProcessingDocs(docs: CatDoc[]): CatDoc[] {
    let processingDocs: CatDoc[] = [];

    (docs ?? []).forEach((doc) => {
      if (doc.isProcessRunning && doc.runNum) {
        processingDocs.push(doc);
      }

      (doc.histDocs ?? []).forEach((histDoc) => {
        if (histDoc.isProcessRunning && histDoc.runNum) {
          processingDocs.push(histDoc);
        }
      });
    });

    return processingDocs;
  }

  private updateLoadedDoc(docId: number, updatedDoc: CatDoc): void {
    let loadedDoc = this.docs.find((d) => d.docId == docId);

    if (!loadedDoc) {
      for (let doc of this.docs) {
        loadedDoc = (doc.histDocs ?? []).find((histDoc) => histDoc.docId == docId);
        if (loadedDoc) {
          break;
        }
      }
    }

    if (loadedDoc) {
      Object.assign(loadedDoc, updatedDoc);
    }
  }

  reloadSingleDoc(docId: number) {
    let searchParams = new CatDocsSearchParams();
    searchParams.docId = docId;

    this.catService
      .getCatDocs(searchParams, this.curCat.catVersionId, false)
      .subscribe((data) => {
        if (data?.[0]) {
          this.updateLoadedDoc(docId, data[0]);
        }
      });
  }

  handleProcessingDocs(docs: CatDoc[]) {
    docs.forEach((d) => {
      let timer = setInterval(() => {
        this.authService.getProcessStatus(d.runNum).subscribe(
          (data) => {
            d.processStatus = data;
            let switched_to_pending_checks = d.runStatusCode == ProcessRunStatusEnum.RUNNING && data.status == ProcessRunStatusEnum.PENDING_CHECKS
            d.runStatusCode = data.status as string;
            if (
              data.status == ProcessRunStatusEnum.FINISHED ||
              data.status == ProcessRunStatusEnum.FAILED
            ) {
              clearInterval(timer);
              this.reloadSingleDoc(d.docId);
              if (this.curDoc && this.curDoc?.docId == d.docId) {
                this.displayProcessStatus = false;
              }
            }
            if (switched_to_pending_checks) {
              this.reloadSingleDoc(d.docId)
            }
          },
          (err) => {
            this.getProcStatusFailedCnt++;
            if (this.getProcStatusFailedCnt > 20) {
              clearInterval(timer);
            }
          }
        );
      }, this.getProcessStatusIntervalSeconds * 1000);

      this.processStatusTimers.push(timer);
    });
  }

  onPagination(event: PaginationParams) {
    if (
      !this.docs ||
      (event.rangeFrom == this.searchParams.rangeFrom &&
        event.rangeTo == this.searchParams.rangeTo)
    )
      return;

    this.searchParams.rangeFrom = event.rangeFrom;
    this.searchParams.rangeTo = event.rangeTo;
    this.searchParams.searchId =
      this.ngRedux.getState().catDocsMetadata.searchId;

    this.loadDocs(false);
  }

  onFilterKeyPress(e: any) {
    if (e.keyCode == keyCodes.enter) {
      this.loadDocs(true);
    }
  }

  onFieldValueChanged(e: any) {
    if (e != null) (e.originalEvent as MouseEvent).stopPropagation();
    this.loadDocs(true);
  }

  clearFilter() {
    this.filterDocIdName = null;
    this.filterStatus = null;
    this.filterInactive = false;
    this.loadDocs(true);
  }

  view(doc: CatDoc) {
        this.commonService.getImageCategoriesForDocument(doc.docId, false).subscribe(() => {
          
          this.viewDocProcessing(doc);
        })

  }
  viewDocProcessing(doc: CatDoc) {

    let viewDoc;
    if (doc.histDocs.length > 0) {
      viewDoc = doc.histDocs.find((d) => d.versionId == doc.lastVersionId);
    } else {
      viewDoc = doc;
    }

    this.ngRedux.dispatch({
      type: actionsList.SELECT_CAT_DOC,
      data: viewDoc,
      catDocSTateTag: CatDocStateTagEnum.PRIME,
    });

    if (this.selectMode) {
      this.router.navigate([this.viewDocNavUrl, this.navMode]);
    } else {
      if (doc.versionId != null) {

        this.router.navigate([this.sharedService.routeEnum.URL_CAT_DOC, this.curCat.catId, this.curCat.catVersionId, viewDoc.docId], { queryParams: { versionId: viewDoc.versionId } });
        return; 
      }
      this.router.navigate([this.sharedService.routeEnum.URL_CAT_DOC, this.curCat.catId, this.curCat.catVersionId, viewDoc.docId]);

    }
  }

  detach(doc: CatDoc) {
    let detachDoc;
    if (doc.histDocs.length > 0) {
      detachDoc = doc.histDocs.find((d) => d.versionId == doc.lastVersionId);
    } else {
      detachDoc = doc;
    }

    if (detachDoc.attachedCardSw) {
      let validationStr = this.translateService.instant(
        'translate.cat.detachWithCardWarning'
      );

      this.sharedService.emitPopupchReceivedEvent({
        title: 'translate.validation.confirm',
        icon: 'exclamation-triangle',
        subTitle: validationStr,
        keep: false,
        approve: () => {
          this.updateDocStatus(detachDoc, CatDocStatusEnum.DETACHED);
        },
      });
    } else this.updateDocStatus(detachDoc, CatDocStatusEnum.DETACHED);
  }

  reopen(doc: CatDoc) {
    let reopenDoc;
    if (doc.histDocs.length > 0) {
      reopenDoc = doc.histDocs.find((d) => d.versionId == doc.lastVersionId);
    } else {
      reopenDoc = doc;
    }

    this.updateDocStatus(reopenDoc, CatDocStatusEnum.DRAFT);
  }

  updateDocStatus(doc: CatDoc, statusCode: string) {
    this.loading = true;
    let catVersionId = doc.catVersionId;
    let docId = doc.docId;
    switch (statusCode) {
      case CatDocStatusEnum.DETACHED: {
        let asset_type = doc.assetType.label;
        this.integrationService.removeDocumentFromLibrary(catVersionId, docId, asset_type).subscribe(
          (data) => {
            this.catService.processUpdIndDocResponse(data);
            this.loading = false;
          },
          () => (this.loading = false)
        );
        break;
      }
      case CatDocStatusEnum.DRAFT: {
        this.integrationService.reopenLibraryDocument(catVersionId, docId).subscribe(
          (data) => {
            this.catService.processUpdIndDocResponse(data);
            this.loading = false;
          },
          () => (this.loading = false)
        );
        break;
      }
      default: {
        this.loading = false;
        break;
      }
    }
  }

  copyToOtherCat(doc: CatDoc) {}

  allowDetach(doc: CatDoc): boolean {
      return doc.statusCode == CatDocStatusEnum.ADDED_TO_CATALOG
          && CatalogStatusEnum.PUBLISHED != this.curCat.statusCode;
  }

  allowReopen(doc: CatDoc): boolean {
    return doc.statusCode != CatDocStatusEnum.DRAFT;
  }

  allowAddVersion(doc: CatDoc): boolean {
    return doc.statusCode == CatDocStatusEnum.DRAFT;
  }

  onDocCreated(doc: CatDoc) {
    this.displayAddCatDoc = false;
    if (!this.selectMode) this.loadDocs(true);
  }

  onAllDocsCreated(docs: CatDocFileWrapper[]) {
    if (this.selectMode) {
      this.onDocsSelected.emit(docs.map((d) => d.doc));
    }
  }

  openProcessStatus(doc: CatDoc, event: Event) {
    
    event.stopPropagation();
    this.displayProcessStatus = true;
    this.curDoc = doc;
  }

  onDocSelectionChanged(rows: CatDoc[]) {
    this.selectedDocs = rows;
  }

  onVersionSelectionChanged(rows: CatDoc[]) {
    this.selectedVersions = rows;
  }

  tagDocs() {
    let docIds = [
      ...new Set(
        [...this.selectedDocs, ...this.selectedVersions].map(
          (item) => item.origDocId
        )
      ),
    ];

    this.loading = true;
    this.catService.addTaggedDocs(docIds, 'IND').subscribe(
      (data) => {
        this.tblDocs?.resetSelection();
        this.tblVersions?.resetSelection();
        this.selectedDocs.forEach((d) => (d.taggedSw = true));
        this.selectedVersions.forEach((d) => (d.taggedSw = true));
        this.selectedDocs = [];
        this.selectedVersions = [];
        this.loading = false;
      },
      (err) => (this.loading = false)
    );
  }

  docsSelected() {
    this.onDocsSelected.emit(this.selectedDocs);
  }

  goBack() {
    this.location.back();
  }

  onClickEditDocName(event: Event, doc: CatDoc) {
    event.stopPropagation();
    this.docsBeingEdited[doc.origDocId] = { docName: doc.docName }
  }

  onClickCancelEditDocName(event: Event, docId: number) {
    event.stopPropagation();
    delete this.docsBeingEdited[docId];
  }

  onClickUpdateDocName(event: Event, docId: number) {
    event.stopPropagation();

    this.docsBeingUpdated[docId] = this.docsBeingEdited[docId];

    const docName = this.docsBeingUpdated[docId].docName;

    this.catService.renameCatDoc(docId, docName).subscribe(
      _ => {
        delete this.docsBeingUpdated[docId];
        delete this.docsBeingEdited[docId];

        let doc = this.docs.find(d => d.origDocId === docId);
        doc.docName = docName;
        doc.histDocs.forEach(hd => hd.docName = docName);
      },
      err => {
        delete this.docsBeingUpdated[docId];
        delete this.docsBeingEdited[docId];

        this.sharedService.showInfoMessage({infoType: 'error', messageKey: err,  showToast: false})
      }
    );
  }
  checkProcessStatusAndOpen(doc: CatDoc, event: Event) {
    if (doc.processStatus) {
      this.openProcessStatus(doc, event);
    }
  }
}
